// Key numbers against Safrati's own records. A tile says "12k+ Happy
// customers"; the CMS reads the figure, finds the live count it claims to
// be (stats/get, the numbers Home shows, counted the same way: one per
// application, with the same test-account switch; CMS review rania-06), and
// says when the tile claims more than the records hold. It never changes a
// number.
import { applicationCounts } from "src/views/dashboard/numbers";

const fmt = (n) => new Intl.NumberFormat("en-US").format(n);

// "12k+" → { value: 12000, plus: true }, "1,200" → { value: 1200 },
// "1.5k" → 1500, "2M+" → 2000000, "95%" → { value: 95, percent: true }.
// Null when there is no number to read.
export function parseFigure(text) {
  const raw = String(text ?? "").trim();
  const m = /^(?:over|more than|about|~)?\s*([0-9][0-9,]*(?:\.[0-9]+)?)\s*([km])?\s*(\+|%)?\s*$/i.exec(raw);
  if (!m) return null;
  const digits = m[1].replace(/,/g, "");
  // "1,200" groups thousands; "1,2" is not a figure anyone means.
  if (m[1].includes(",") && !/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(m[1])) return null;
  let value = Number(digits);
  if (!Number.isFinite(value)) return null;
  const scale = (m[2] || "").toLowerCase();
  if (scale === "k") value *= 1000;
  if (scale === "m") value *= 1000000;
  return { value: Math.round(value), plus: m[3] === "+" || /^(over|more than)/i.test(raw), percent: m[3] === "%" };
}

// What a label claims to count, matched to a live count from stats/get.
// The first rule that fits wins; null when Safrati's records can't say
// ("Years of experience", "Countries").
const RULES = [
  { test: /approv|granted|issued/i, words: "approved applications", count: (s) => (s?.requests ? applicationCounts(s.requests).status.approved ?? 0 : undefined) },
  { test: /customer|client|traveller|traveler|user|people|famil/i, words: "customer accounts", count: (s) => s?.users?.total },
  { test: /support|chat|conversation|help/i, words: "support chats", count: (s) => s?.supportChatCount },
  {
    test: /application|request|visa|processed|submitted/i,
    words: "submitted applications",
    // Drafts were never sent, so they don't count as applications handled.
    count: (s) => {
      if (!s?.requests) return undefined;
      const counted = applicationCounts(s.requests);
      return counted.total - Number(counted.status.incomplete || 0);
    },
  },
];

export function liveCountFor(label, stats) {
  const rule = RULES.find((r) => r.test.test(String(label || "")));
  if (!rule) return null;
  const n = rule.count(stats);
  return n == null || !Number.isFinite(Number(n)) ? null : { value: Number(n), words: rule.words };
}

// "12k+ is more than the 242 customer accounts", or null when the number is
// within the records (or there is nothing to compare it with).
export function figureWarning({ description, title }, stats) {
  const figure = parseFigure(description);
  if (!figure || figure.percent) return null;
  const live = liveCountFor(title, stats);
  if (!live) return null;
  if (figure.value <= live.value) return null;
  return `${String(description).trim()} is more than the ${fmt(live.value)} ${live.words}`;
}

// A one-word label ("Approval", "Support") doesn't say what is counted.
export const vagueLabel = (title) => {
  const words = String(title || "").trim().split(/\s+/).filter(Boolean);
  return words.length === 1;
};
